/**
 * API hook for the calendar (therapist HTTP API, JWT authorizer).
 * Spec §3 names this concept `useAppointments`; renamed to match the
 * repo's `useAssessmentsApi` naming convention.
 *
 * Every mutator resolves to the server's MutationResponse so callers can
 * surface `warnings` (overlap, email skips) — never swallowed.
 */
import { useCallback, useMemo } from "react";
import { API_BASE } from "../../config";
import { useAuth } from "../../auth/AuthProvider";
import type {
  CreateAppointmentPayload,
  EditScope,
  InvoicingSettings,
  MutationResponse,
  SchedulingSettings,
  WeekResponse,
} from "./types";

export function useCalendarApi() {
  const { ensureToken } = useAuth();

  // HTTP API helper (JWT authorizer) — same shape as useAssessmentsApi.apiJson
  const apiJson = useCallback(async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
    const token = await ensureToken();
    const resp = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.body ? { "content-type": "application/json" } : {}),
        ...(init.headers || {}),
      },
    });

    const text = await resp.text();
    let data: any = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      console.error("[useCalendarApi] JSON parse failed:", { path, status: resp.status, text });
      throw new Error(`HTTP ${resp.status} (non-JSON response)`);
    }

    if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
    return data as T;
  }, [ensureToken]);

  const loadWeek = useCallback(
    (fromIso: string, toIso: string, signal?: AbortSignal): Promise<WeekResponse> => {
      const qs = new URLSearchParams({ from: fromIso, to: toIso });
      return apiJson<WeekResponse>(`/appointments?${qs.toString()}`, { signal });
    },
    [apiJson],
  );

  const create = useCallback(
    (payload: CreateAppointmentPayload): Promise<MutationResponse> =>
      apiJson<MutationResponse>("/appointments", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    [apiJson],
  );

  const patch = useCallback(
    (id: string, body: Record<string, unknown>, scope?: EditScope): Promise<MutationResponse> => {
      const qs = scope ? `?scope=${scope}` : "";
      return apiJson<MutationResponse>(`/appointments/${encodeURIComponent(id)}${qs}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
    },
    [apiJson],
  );

  const move = useCallback(
    (id: string, startAtIso: string): Promise<MutationResponse> =>
      // Drag always moves ONE occurrence (detaches it server-side); series
      // edits go through the dialog's explicit scope choice (spec §3).
      patch(id, { start_at: startAtIso }),
    [patch],
  );

  const resize = useCallback(
    (id: string, durationMin: number): Promise<MutationResponse> =>
      patch(id, { duration_min: durationMin }),
    [patch],
  );

  const cancel = useCallback(
    (id: string, notify?: boolean): Promise<MutationResponse> =>
      patch(id, { status: "cancelled", ...(notify ? { notify: true } : {}) }),
    [patch],
  );

  const remove = useCallback(
    (id: string, scope?: EditScope, notify?: boolean): Promise<MutationResponse> => {
      const params = new URLSearchParams();
      if (scope) params.set("scope", scope);
      if (notify) params.set("notify", "1");
      const qs = params.toString() ? `?${params.toString()}` : "";
      return apiJson<MutationResponse>(`/appointments/${encodeURIComponent(id)}${qs}`, {
        method: "DELETE",
      });
    },
    [apiJson],
  );

  const gcalStatus = useCallback(
    (signal?: AbortSignal): Promise<{ connected: boolean; failed_count: number }> =>
      apiJson("/gcal/status", { signal }),
    [apiJson],
  );

  const gcalConnect = useCallback(
    (): Promise<{ auth_url: string }> => apiJson("/gcal/connect", { method: "POST" }),
    [apiJson],
  );

  const gcalDisconnect = useCallback(
    (): Promise<MutationResponse> => apiJson("/gcal/disconnect", { method: "DELETE" }),
    [apiJson],
  );

  const loadSettings = useCallback(
    (signal?: AbortSignal): Promise<SchedulingSettings> =>
      apiJson<SchedulingSettings>("/settings/scheduling", { signal }),
    [apiJson],
  );

  const saveSettings = useCallback(
    (s: SchedulingSettings): Promise<MutationResponse> =>
      apiJson<MutationResponse>("/settings/scheduling", {
        method: "PUT",
        body: JSON.stringify(s),
      }),
    [apiJson],
  );

  const loadInvoicing = useCallback(
    (signal?: AbortSignal): Promise<InvoicingSettings> =>
      apiJson<InvoicingSettings>("/settings/invoicing", { signal }),
    [apiJson],
  );

  const saveInvoicing = useCallback(
    (s: Partial<InvoicingSettings> & { agent_key?: string }): Promise<MutationResponse & { settings: InvoicingSettings }> =>
      apiJson("/settings/invoicing", { method: "PUT", body: JSON.stringify(s) }),
    [apiJson],
  );

  const createInvoice = useCallback(
    (id: string, grossHuf: number): Promise<MutationResponse & {
      invoice: { invoice_number: string; invoice_gross: number; invoice_url: string };
    }> =>
      apiJson(`/appointments/${encodeURIComponent(id)}/invoice`, {
        method: "POST",
        body: JSON.stringify({ gross_huf: grossHuf }),
      }),
    [apiJson],
  );

  const stornoInvoice = useCallback(
    (id: string): Promise<MutationResponse & { invoice: { invoice_storno_number: string } }> =>
      apiJson(`/appointments/${encodeURIComponent(id)}/invoice`, { method: "DELETE" }),
    [apiJson],
  );

  return useMemo(
    () => ({ loadWeek, create, patch, move, resize, cancel, remove, loadSettings,
             saveSettings, loadInvoicing, saveInvoicing, createInvoice, stornoInvoice,
             gcalStatus, gcalConnect, gcalDisconnect }),
    [loadWeek, create, patch, move, resize, cancel, remove, loadSettings,
     saveSettings, loadInvoicing, saveInvoicing, createInvoice, stornoInvoice,
     gcalStatus, gcalConnect, gcalDisconnect],
  );
}

export type CalendarApi = ReturnType<typeof useCalendarApi>;
